import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DateTime } from "luxon";
import { FaArrowLeft, FaCalendarAlt, FaCheckCircle } from "react-icons/fa";
import { api } from "../../api/api";
import { NavBar } from "../../components/NavBar";
import { extractPixValue, maskCurrency } from "../../utils/utils";
import "./style.css";

export function CourseDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    async function getCourse() {
      try {
        const response = await api.get("/api/v1/courses");
        const selectedCourse = response.data.data.find(
          (item) => String(item.id) === String(id),
        );

        setCourse(selectedCourse ?? null);
        setNotFound(!selectedCourse);
      } catch (error) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }

    getCourse();
  }, [id]);

  const enrollmentClosed = course
    ? course.active !== true ||
      DateTime.fromSQL(course.startDate).diff(DateTime.local(), "days").days < 0
    : false;

  return (
    <NavBar>
      <div className="course-detail-page">
        <button className="course-back" type="button" onClick={() => navigate("/home")}>
          <FaArrowLeft /> Voltar para os cursos
        </button>

        {loading && (
          <div className="empty-state surface-card">
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-3 mb-0">Carregando informações do curso...</p>
          </div>
        )}

        {!loading && notFound && (
          <div className="empty-state surface-card course-not-found">
            <h1>Curso não encontrado</h1>
            <p>Este curso não está mais disponível ou o endereço acessado é inválido.</p>
            <button className="btn btn-primary" type="button" onClick={() => navigate("/home")}>
              Ver todos os cursos
            </button>
          </div>
        )}

        {!loading && course && (
          <>
            <section className="course-hero surface-card">
              <div className="course-hero-image">
                <img src={course.url || "/img/curso-virtual-jpg.jpg"} alt={course.name} />
              </div>
              <div className="course-hero-content">
                <span className={`course-status ${enrollmentClosed ? "closed" : "open"}`}>
                  {enrollmentClosed ? "Matrículas encerradas" : "Matrículas abertas"}
                </span>
                <h1>{course.name}</h1>
                <div className="course-period">
                  <FaCalendarAlt />
                  <span>
                    {DateTime.fromSQL(course.startDate).toFormat("dd/MM/yyyy")} a{" "}
                    {DateTime.fromSQL(course.endDate).toFormat("dd/MM/yyyy")}
                  </span>
                </div>
                <div className="course-facts">
                  {course.periodo && (
                    <div>
                      <span>Período</span>
                      <strong>{course.periodo}</strong>
                    </div>
                  )}
                  {course.duration && (
                    <div>
                      <span>Duração</span>
                      <strong>{course.duration}</strong>
                    </div>
                  )}
                  {course.studantsLimit != null && (
                    <div>
                      <span>Limite de alunos</span>
                      <strong>{course.studantsLimit}</strong>
                    </div>
                  )}
                </div>
                {!enrollmentClosed && (
                  <button
                    className="btn btn-primary course-enroll"
                    type="button"
                    onClick={() => navigate(`/subscription?id=${course.id}`)}
                  >
                    Quero me matricular
                  </button>
                )}
              </div>
            </section>

            <div className="course-detail-layout">
              <section className="course-about surface-card">
                <span className="course-section-kicker">Sobre o curso</span>
                <h2>Conheça esta formação</h2>
                <div className="course-full-description">{course.description}</div>
              </section>

              <aside className="course-pricing surface-card">
                <span className="course-section-kicker">Investimento</span>
                <h2>Valores do curso</h2>

                <div className="pricing-group">
                  <h3>Curso</h3>
                  <div className="pricing-row">
                    <span>À vista</span>
                    <strong>{maskCurrency(course.pricingTotal)}</strong>
                  </div>
                  <div className="pricing-row">
                    <span>Parcelado</span>
                    <strong>
                      {course.qtdInstallments}x de {maskCurrency(course.pricingInstallments)}
                    </strong>
                  </div>
                  {Number(course.pricingTerm) > 0 && (
                    <div className="pricing-row">
                      <span>Valor a prazo</span>
                      <strong>{maskCurrency(course.pricingTerm)}</strong>
                    </div>
                  )}
                </div>

                <div className="pricing-group">
                  <h3>Material</h3>
                  {Number(course.materialPrice) > 0 && (
                    <div className="pricing-row">
                      <span>Material</span>
                      <strong>{maskCurrency(course.materialPrice)}</strong>
                    </div>
                  )}
                  <div className="pricing-row">
                    <span>Material Ômega à vista</span>
                    <strong>{maskCurrency(course.materialPriceOmega)}</strong>
                  </div>
                  <div className="pricing-row">
                    <span>Parcelado</span>
                    <strong>
                      {course.qtdInstallmentsMaterialOmega}x de{" "}
                      {maskCurrency(course.pricingInstallmentsMaterialOmega)}
                    </strong>
                  </div>
                </div>

                <div className="enrollment-price">
                  <span>Valor da matrícula</span>
                  <strong>{maskCurrency(extractPixValue(course.pix))}</strong>
                </div>

                {!enrollmentClosed && (
                  <div className="availability-note">
                    <FaCheckCircle /> Inscrições disponíveis
                  </div>
                )}
              </aside>
            </div>
          </>
        )}
      </div>
    </NavBar>
  );
}
